import { useCallback, useEffect, useRef, useState } from "react";
import { FetchOptions, FetchState } from "./types";
import { useUserSessionContext } from "../../contexts/UserSessionContext";
import { UseFetchProps, UseFetchReturn } from "../types";
import { HttpErrorType } from "../../types";
import { buildFetchOptions, handleFetchError } from "../../utils";

export function useFetch<T>({ method = "GET", url, serializer, skipAuth }: UseFetchProps<T>): UseFetchReturn<T> {
  const { userSession } = useUserSessionContext();
  const accessTokenRef = useRef(userSession?.accessToken);
  const serializerRef = useRef(serializer);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [state, setState] = useState<FetchState<T>>({
    data: null,
    error: null,
    loading: false,
  });

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    accessTokenRef.current = userSession?.accessToken;
  }, [userSession?.accessToken]);

  useEffect(() => {
    serializerRef.current = serializer;
  }, [serializer]);

  const fetchData = useCallback(
    async (options?: FetchOptions) => {
      abort();
      abortControllerRef.current = new AbortController();

      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const mergedUrl = options?.url || url;
        if (!mergedUrl) {
          throw Error(`fetch cannot be invoked with empty url`);
        }

        const response = await fetch(
          mergedUrl,
          buildFetchOptions({
            options,
            method,
            signal: abortControllerRef.current.signal,
            token: accessTokenRef.current,
            skipAuth,
          }),
        );

        if (!response.ok) {
          await handleFetchError(response);
        }

        const serializeData: T | null = await serializerRef.current(response);

        setState({
          data: serializeData,
          error: null,
          loading: false,
        });

        return { data: serializeData };
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          return null;
        }

        setState({
          data: null,
          error: error as HttpErrorType,
          loading: false,
        });

        throw error;
      }
    },
    [url, abort, method, skipAuth],
  );

  useEffect(() => {
    return () => {
      abort();
    };
  }, [abort]);

  return {
    ...state,
    fetch: fetchData,
    abort,
  };
}
